// =====================================================================
// Tema claro/escuro do portal. Grava data-theme no <html> (os tokens em
// tokens.css reagem a isso) e persiste a escolha. Primeira carga respeita
// a preferência do sistema (prefers-color-scheme).
// =====================================================================
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Liga/desliga o tema escuro do portal inteiro. Enquanto for `false`, o site
// fica sempre claro: ignora localStorage e a preferência do sistema, o toggle
// vira no-op e o botão ThemeToggle nem aparece (ele lê este flag). Pra reativar
// no futuro é só voltar pra `true` — nada do código de tema escuro foi removido.
export const TEMA_ESCURO_ATIVO = false;

const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });
const STORAGE_KEY = '@UEB:theme';

function temaInicial() {
    if (!TEMA_ESCURO_ATIVO) return 'light';
    try {
        const salvo = localStorage.getItem(STORAGE_KEY);
        if (salvo === 'light' || salvo === 'dark') return salvo;
    } catch { /* ignora */ }
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(temaInicial);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignora */ }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        if (!TEMA_ESCURO_ATIVO) return;
        setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
    }, []);

    const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    return useContext(ThemeContext);
}
